export const ENTITY_KIND_ROAD = 'road';
export const ENTITY_KIND_PARKING = 'parking';
export const ENTITY_KIND_RESIDENTIAL_BUILDING = 'residential';
export const ENTITY_KIND_WORK_BUILDING = 'work';
export const ENTITY_KIND_PUBLIC_BUILDING = 'public';
export const ENTITY_KIND_PARK = 'park';
export const ENTITY_KIND_PAVED = 'paved';
export const ENTITY_KIND_GARDEN = 'garden';
export const ENTITY_KIND_MARKET = 'market';
export const ENTITY_KIND_POWER = 'power';
export const ENTITY_KIND_LAND = 'land';

export const ALL_ENTITY_KINDS = [
  ENTITY_KIND_ROAD,
  ENTITY_KIND_PARKING,
  ENTITY_KIND_RESIDENTIAL_BUILDING,
  ENTITY_KIND_WORK_BUILDING,
  ENTITY_KIND_PUBLIC_BUILDING,
  ENTITY_KIND_PARK,
  ENTITY_KIND_PAVED,
  ENTITY_KIND_GARDEN,
  ENTITY_KIND_MARKET,
  ENTITY_KIND_POWER,
  ENTITY_KIND_LAND,
];

export const ASSET_MODE_PER_ENTITY = 'per entity';
export const ASSET_MODE_PER_CELL = 'per cell';

export const DIRECTION_NORTH = 'N';
export const DIRECTION_EAST = 'E';
export const DIRECTION_SOUTH = 'S';
export const DIRECTION_WEST = 'W';

export class Point {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
}

export class Entity {
  constructor(id, kind, cellIds, assetIds, directions, props = {}) {
    console.assert(cellIds.length === assetIds.length, `Entity ${id}: cell IDs and asset IDs should have the same length.`);
    console.assert(cellIds.length === directions.length, `Entity ${id}: cell IDs and directions should have the same length.`);

    this.id = id;
    this.kind = kind;
    this.cellIds = cellIds;
    this.assetIds = assetIds;
    this.directions = directions;
    this.props = Entity.defaultPropsForKind(kind);
    if (props) {
      const propNames = Object.keys(props);
      for (const propName of propNames) {
        if (this.props[propName]) {
          this.props[propName].value = props[propName];
        }
      }
    }
  }

  get color() {
    switch (this.kind) {
      case ENTITY_KIND_ROAD:
        return '#ffd5a3';
      case ENTITY_KIND_PARKING:
        return '#aaa';
      case ENTITY_KIND_RESIDENTIAL_BUILDING:
        return '#888';
      case ENTITY_KIND_WORK_BUILDING:
        return '#ccc';
      case ENTITY_KIND_PUBLIC_BUILDING:
        return '#d66';
      case ENTITY_KIND_PARK:
        return '#add19e';
      case ENTITY_KIND_GARDEN:
        const gardenKind = this.props.gardenKind.value;
        if (gardenKind === 'grass') {
          return '#90a259';
        } else if (gardenKind === 'paved') {
          return '#c5b9b1';
        } else if (gardenKind === 'field') {
          return '#b17f4a';
        } else if (gardenKind === 'pasture') {
          return '#68882f';
        } else if (gardenKind === 'community') {
          return '#833c11';
        } else {
          throw new Error(`Invalid gardenKind ${gardenKind}`);
        }
      case ENTITY_KIND_MARKET:
        return '#a2a19f';
      case ENTITY_KIND_POWER:
        const powerKind = this.props.powerKind.value;
        if (powerKind === 'wind') {
          return '#9ab6d3';
        } else {
          throw new Error(`Invalid powerKind ${powerKind}`);
        }
      default:
        return '#ff00ff';
    }
  }

  static fixupAssetIds(id, cellIds, assetIds) {
    if (assetIds.length === 1) {
      // If there is only one asset ID, repeat it for all cell IDs.
      assetIds = Array(cellIds.length).fill(assetIds[0]);
    } else if (cellIds.length === assetIds.length) {
      // Do nothing
    } else {
      throw new Error(`Entity ${id}: cell IDs ${cellIds} do not match asset IDs ${assetIds}.`);
    }
    return assetIds;
  }

  static fixupDirections(id, cellIds, directions) {
    if (directions.length === 1) {
      // If there is only one direction, repeat it for all cell IDs.
      directions = Array(cellIds.length).fill(directions[0]);
    } else if (cellIds.length === directions.length) {
      // Do nothing
    } else {
      throw new Error(`Entity ${id}: cell IDs ${cellIds} do not match directions ${directions}.`);
    }
    return directions;
  }

  static fromJSON(json) {
    const cellIds = json.cells.split(',').map((x) => parseInt(x));
    let assetIds = json.assets.split(',').map((x) => parseInt(x));
    assetIds = Entity.fixupAssetIds(json.id, cellIds, assetIds);
    let directions;
    if (json.directions) {
      directions = json.directions.split('');
      directions = Entity.fixupDirections(json.id, cellIds, directions);
    } else {
      let direction = DIRECTION_NORTH;
      if (json.props && json.props.directions) {
        direction = json.props.direction;
      }
      directions = Array(cellIds.length).fill(direction);
    }
    const entity = new Entity(json.id, json.kind, cellIds, assetIds, directions, json.props);
    return entity;
  }

  static defaultPropsForKind(kind) {
    const props = {};
    props.notes = {
      label: 'Notes',
      type: 'text',
      defaultValue: '',
      value: '',
    };
    return props;
  }

  static assetModeForKind(kind) {
    if (kind === ENTITY_KIND_ROAD || kind === ENTITY_KIND_PARK || kind === ENTITY_KIND_PAVED) {
      return ASSET_MODE_PER_CELL;
    } else {
      return ASSET_MODE_PER_ENTITY;
    }
  }

  toJSON() {
    const json = {};
    json.id = this.id;
    json.kind = this.kind;
    json.cells = this.cellIds.join(',');
    if (Entity.assetModeForKind(json.kind) === ASSET_MODE_PER_CELL) {
      // If all cells share the same asset ID, it can collapse down to a single asset ID.
      if (new Set(this.assetIds).size === 1) {
        json.assets = String(this.assetIds[0]);
      } else {
        json.assets = this.assetIds.join(',');
      }
      if (new Set(this.directions).size === 1) {
        json.directions = this.directions[0];
      } else {
        json.directions = this.directions.join('');
      }
    } else {
      json.assets = String(this.assetIds[0]);
      json.directions = this.directions[0];
    }
    const propNames = Object.keys(this.props);
    for (const propName of propNames) {
      const prop = this.props[propName];
      if (prop.type === 'heading' || prop.type === 'spacer') continue;
      if (prop.value !== prop.defaultValue) {
        json.props = json.props || {};
        json.props[propName] = prop.value;
      }
    }
    return json;
  }

  // Clone the entity. Note that it will have the same ID.
  clone() {
    const propValues = {};
    for (const propName of Object.keys(this.props)) {
      const prop = this.props[propName];
      if (prop.type === 'heading' || prop.type === 'spacer') continue;
      propValues[propName] = JSON.parse(JSON.stringify(prop.value));
    }
    const entity = new Entity(this.id, this.kind, this.cellIds.slice(), this.assetIds.slice(), this.directions.slice(), propValues);
    return entity;
  }

  changeKind(newKind) {
    if (this.kind === newKind) return;
    const newProps = Entity.defaultPropsForKind(newKind);
    const propNames = Object.keys(newProps);
    for (const propName of propNames) {
      // Copy over existing props values.
      if (this.props[propName] !== undefined) {
        newProps[propName].value = this.props[propName].value;
      }
    }
    this.kind = newKind;
    this.props = newProps;
  }

  setPropertyValue(name, value) {
    if (this.props[name] === undefined) {
      throw new Error(`Property ${name} on entity ${this.kind} does not exist.`);
    }
    this.props[name].value = value;
  }
}

class Layer {
  constructor(name, width, height) {
    this.name = name;
    this.width = width;
    this.height = height;
    this.visible = false;
    this.editorVisible = true;
    this.editorExpanded = false;
    this.entities = [];
    this._cellMap = new Uint32Array(width * height);
  }

  _offsetCellId(cellId, dx, dy) {
    let x = cellId % this.width;
    let y = Math.floor(cellId / this.width);
    x += dx;
    y += dy;
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      throw new Error(`Cell id ${cellId} cannot be offset to ${dx} ${dy}`);
    }
    return y * this.width + x;
  }

  addEntity(entity) {
    this.entities.push(entity);
    for (const cellId of entity.cellIds) {
      this._cellMap[cellId] = entity.id;
    }
  }

  findEntityById(id) {
    return this.entities.find((e) => e.id === id);
  }

  findEntitiesForCells(cellIds, entities) {
    if (!entities) entities = new Set();
    for (const cellId of cellIds) {
      const entityId = this._cellMap[cellId];
      const entity = this.findEntityById(entityId);
      if (entity) entities.add(entity);
    }
    return entities;
  }

  findCellIdsForEntities(entities) {
    const cellIds = [];
    for (const entity of entities) {
      cellIds.push(...entity.cellIds);
    }
    return cellIds;
  }

  _moveCellIds(entity, dx, dy) {
    const newCellIds = [];
    for (const cellId of entity.cellIds) {
      const newCellId = this._offsetCellId(cellId, dx, dy);
      newCellIds.push(newCellId);
    }
    entity.cellIds = newCellIds;
  }

  _rebuildCellMap() {
    this._cellMap.fill(0);
    for (const entity of this.entities) {
      for (const cellId of entity.cellIds) {
        this._cellMap[cellId] = entity.id;
      }
    }
  }

  moveEntities(entities, dx, dy) {
    for (const entity of entities) {
      this._moveCellIds(entity, dx, dy);
    }
    this._rebuildCellMap();
  }

  duplicateEntities(world, entities, dx, dy) {
    const newEntities = [];
    for (const entity of entities) {
      const newEntity = entity.clone();
      newEntity.id = world.generateEntityId();
      this._moveCellIds(newEntity, dx, dy);
      newEntities.push(newEntity);
    }
    this.entities.push(...newEntities);
    this._rebuildCellMap();
    return newEntities;
  }

  // Filter the list of cells to those that are not part of an entity.
  filterUnoccupiedCellIds(cellIds) {
    return cellIds.filter((id) => this._cellMap[id] === 0);
  }

  someCellsOccupied(cellIds) {
    return !!cellIds.find((id) => this._cellMap[id] !== 0);
  }

  boxOccupied(minX, minY, maxX, maxY) {
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
          const cellId = y * this.width + x;
          if (this._cellMap[cellId]) return true;
        } else {
          return true;
        }
      }
    }
    return false;
  }

  clone() {
    const newLayer = new Layer(this.name, this.width, this.height);
    for (const entity of this.entities) {
      newLayer.entities.push(entity.clone());
    }
    newLayer._cellMap = this._cellMap.slice();
    return newLayer;
  }
}

export default class World {
  constructor(width, height) {
    this.name = '';
    this.width = width;
    this.height = height;
    this.assetLibrary = undefined;
    this.assets = {};
    this.layers = [];
    this.lastEntityId = 0;
  }

  static isValidName(name) {
    const NAME_RE = /[a-z][a-z1-9]+/;
    return NAME_RE.exec(name);
  }

  static createEmpty(name, width, height, assetLibrary) {
    const world = new World(width, height);
    world.name = name;
    world.assetLibrary = assetLibrary;
    world.layers.push({
      name: 'base',
      entities: [],
      visible: true,
      editorVisible: true,
    });
    return world;
  }

  static fromJSON(json) {
    const world = new World(json.width, json.height);
    world.name = json.name;
    world.assetLibrary = json.assetLibrary;
    world.assets = json.assets;
    world.layers = [];
    for (const jsonLayer of json.layers) {
      const layer = new Layer(jsonLayer.name, world.width, world.height);
      layer.editorVisible = !!jsonLayer.editorVisible;
      for (let i = 0, l = jsonLayer.entities.length; i < l; i++) {
        const entity = Entity.fromJSON(jsonLayer.entities[i]);
        if (world.entityWithIdExists(entity.id)) {
          throw new Error(`Entity with id ${entity.id} already exists.`);
        }
        layer.addEntity(entity);
        world.lastEntityId = Math.max(world.lastEntityId, entity.id);
      }
      world.layers.push(layer);
    }
    world.settings = json.settings || {};
    return world;
  }

  toJSON() {
    const json = {};
    json.name = this.name;
    json.width = this.width;
    json.height = this.height;
    json.settings = JSON.parse(JSON.stringify(this.settings || {}));
    json.assetLibrary = this.assetLibrary;
    json.assets = JSON.parse(JSON.stringify(this.assets));
    json.layers = [];
    for (const layer of this.layers) {
      const jsonEntities = layer.entities.map((entity) => entity.toJSON());
      const jsonLayer = { name: layer.name, entities: jsonEntities, editorVisible: layer.editorVisible };
      json.layers.push(jsonLayer);
    }
    return json;
  }

  clone() {
    const newWorld = new World(this.width, this.height);
    newWorld.name = this.name;
    newWorld.settings = JSON.parse(JSON.stringify(this.settings || {}));
    newWorld.assetLibrary = this.assetLibrary;
    newWorld.assets = JSON.parse(JSON.stringify(this.assets));
    for (const layer of this.layers) {
      newWorld.layers.push(layer.clone());
    }
    return newWorld;
  }

  createLayer(layerName) {
    if (this.layers.find((l) => l.name === layerName)) return null;
    const layer = new Layer(layerName, this.width, this.height);
    this.layers.unshift(layer);
    return layer;
  }

  generateEntityId() {
    this.lastEntityId += 1;
    return this.lastEntityId;
  }

  findEntityById(id) {
    for (const layer of this.layers) {
      const entity = layer.entities.find((e) => e.id === id);
      if (entity) return entity;
    }
  }

  findAssetNameById(assetId) {
    const assetName = this.assets[assetId];
    if (!assetName) {
      throw new Error(`Could not find asset with id ${assetId} in ${JSON.stringify(this.assets)}`);
    }
    return assetName;
  }

  findAssetIdByName(assetName) {
    for (const [k, v] of Object.entries(this.assets)) {
      if (v === assetName) return parseInt(k);
    }
    return null;
  }

  ensureAsset(assetName) {
    for (const [k, v] of Object.entries(this.assets)) {
      if (v === assetName) {
        return k;
      }
    }
    let maxId = 0;
    if (Object.keys(this.assets).length > 0) {
      maxId = Math.max.apply(null, Object.keys(this.assets));
    }
    // Create new asset ID.
    maxId += 1;
    this.assets[maxId] = assetName;
    return maxId;
  }

  entityWithIdExists(id) {
    return this.findEntityById(id) !== undefined;
  }

  findLayerForEntity(entity) {
    return this.layers.find((layer) => layer.entities.includes(entity));
  }

  extendEntity(entity, cellIds) {
    console.assert(entity.cellIds.length === entity.assetIds.length);
    console.assert(entity.cellIds.length > 0);
    const layer = this.findLayerForEntity(entity);
    console.assert(layer);
    const newCellIds = cellIds.filter((id) => !entity.cellIds.includes(id));
    const newAssetIds = Array(newCellIds.length).fill(entity.assetIds[0]);
    const newDirections = Array(newCellIds.length).fill(DIRECTION_NORTH);
    entity.cellIds.push(...newCellIds);
    entity.assetIds.push(...newAssetIds);
    entity.directions.push(...newDirections);
    newCellIds.forEach((id) => (layer._cellMap[id] = entity.id));
    console.assert(entity.cellIds.length === entity.assetIds.length);
  }

  removeCellsFromEntity(entity, cellIds) {
    cellIds = Array.from(cellIds);
    const layer = this.findLayerForEntity(entity);
    console.assert(layer);
    console.assert(entity.cellIds.length === entity.assetIds.length);
    console.assert(entity.cellIds.length === entity.directions.length);
    const oldLength = entity.cellIds.length;
    for (const id of cellIds) {
      const cellIndex = entity.cellIds.indexOf(id);
      console.assert(cellIndex !== -1, `Could not find cell ${id} in ${entity.id}.`);
      entity.cellIds.splice(cellIndex, 1);
      entity.assetIds.splice(cellIndex, 1);
      entity.directions.splice(cellIndex, 1);
      layer._cellMap[id] = 0;
    }
    const newLength = entity.cellIds.length;
    console.assert(oldLength - cellIds.length === newLength);
    console.assert(entity.cellIds.length === entity.assetIds.length);
    console.assert(entity.cellIds.length === entity.directions.length);
    if (entity.cellIds.length === 0) {
      layer.entities = layer.entities.filter((e) => e !== entity);
    }
  }

  clearCells(cellIds) {
    const entities = this.findEntitiesForCells(cellIds);
    for (const entity of entities) {
      this.removeCellsFromEntity(entity, cellIds);
    }
  }

  _markCell(pt, id) {
    this._cellMap[pt.y * this.width + pt.x] = id;
  }

  _idForCell(pt) {
    return this._cellMap[pt.y * this.width + pt.x];
  }

  pointToEntityId(pt) {
    if (pt.x >= 0 && pt.x < this.width && pt.y >= 0 && pt.y < this.height) {
      for (const layer of this.layers) {
        const entityId = layer._cellMap[pt.y * this.width + pt.x];
        if (entityId !== 0) return entityId;
      }
    }
    return 0;
  }

  pointToEntity(pt) {
    if (pt.x >= 0 && pt.x < this.width && pt.y >= 0 && pt.y < this.height) {
      for (const layer of this.layers) {
        const entityId = this._cellMap[pt.y * this.width + pt.x];
        if (entityId !== 0) {
          return layer.findEntityById(entityId);
        }
      }
    }
    return null;
  }

  isCellOwnedBy(pt, ownerId) {
    if (pt.x >= 0 && pt.x < this.width && pt.y >= 0 && pt.y < this.height) {
      const entityId = this._cellMap[pt.y * this.width + pt.x];
      if (entityId === 0) return false;
      if (entityId === ownerId) return true;
      const entity = this.getEntity(entityId);
      if (entity.props.owners) {
        const owners = entity.props.owners.value.split(',').map((x) => parseInt(x));
        return owners.includes(ownerId);
      }
      const otherEntity = this.getEntity(ownerId);
      if (otherEntity.props.owners) {
        const owners = otherEntity.props.owners.value.split(',').map((x) => parseInt(x));
        return owners.includes(entityId);
      }
    }
    return false;
  }

  cellExists(pt) {
    return pt.x >= 0 && pt.x < this.width && pt.y >= 0 && pt.y < this.height;
  }

  pointToCellId(pt) {
    if (pt.x >= 0 && pt.x < this.width && pt.y >= 0 && pt.y < this.height) {
      return pt.y * this.width + pt.x;
    } else {
      return -1;
    }
  }

  xyToCellId(x, y) {
    if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
      return y * this.width + x;
    } else {
      return -1;
    }
  }

  cellIdMoved(id, dx, dy) {
    const pt = this.cellIdToPoint(id);
    pt.x += dx;
    pt.y += dy;
    return this.pointToCellId(pt);
  }

  cellIdToPoint(id) {
    return new Point(id % this.width, Math.floor(id / this.width));
  }

  // Return cell IDs for the given box.
  boxCellIds(minX, minY, maxX, maxY) {
    let cellIds = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
          const cellId = y * this.width + x;
          cellIds.push(cellId);
        }
      }
    }
    return cellIds;
  }

  offsetCellIds(cellIds, dx, dy) {
    const { width, height } = this;
    const newCellIds = [];
    let valid = true;
    for (const cellId of cellIds) {
      let x = cellId % width;
      let y = Math.floor(cellId / width);
      x += dx;
      y += dy;
      if (x < 0 || x >= width || y < 0 || y >= height) {
        newCellIds.push(-1);
        valid = false;
        continue;
      }
      newCellIds.push(y * width + x);
    }
    return [newCellIds, valid];
  }

  // Given a list of cellIds, find the entities that are
  // contained within these cells.
  // Returns a Set of Entity objects.
  findEntitiesForCells(cellIds) {
    if (!cellIds) return new Set();
    const entities = new Set();
    for (const layer of this.layers) {
      layer.findEntitiesForCells(cellIds, entities);
    }
    return entities;
  }

  // Filter the list of cells to those that are not part of an entity.
  filterUnoccupiedCellIds(cellIds) {
    for (const layer of this.layers) {
      cellIds = cellIds.filter((id) => layer._cellMap[id] === 0);
    }
    return cellIds;
  }

  // Get the list of all visible entities without overlapping cells
  getMergedEntities() {
    const entities = [];
    const cellMap = new Uint32Array(this.width * this.height);
    for (const layer of this.layers) {
      if (!layer.visible) continue;
      for (const entity of layer.entities) {
        const assetMode = Entity.assetModeForKind(entity.kind);
        if (assetMode === ASSET_MODE_PER_ENTITY) {
          // All cells of the entity should be unoccupied for it to show up.
          // This only applies for assets that render per entity.
          // Per-cell entities are returned together because they can replace parts.
          const isOccupied = !!entity.cellIds.find((cellId) => !!cellMap[cellId]);
          if (isOccupied) continue;
        }
        entities.push(entity);
        for (const cellId of entity.cellIds) {
          cellMap[cellId] = entity.id;
        }
      }
    }
    return entities.reverse();
  }

  zIndexForCellId(cellId) {
    return this.height - this.cellIdToPoint(cellId).y;
  }

  getEntityBoundingBox(entity, checkCells = true) {
    const bbox = this.calculateBoundingBoxForCells(entity.cellIds);
    if (checkCells) {
      // Check if all cells within the bounding box belong to the entity. Otherwise, return null.
      const { minX, maxX, minY, maxY } = bbox;
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const cellId = y * this.width + x;
          if (this._cellMap[cellId] !== entity.id) {
            console.warn(`Entity ${entity.id} not fully in [${minX} ${minY} - ${maxX} ${maxY}]`);
            return null;
          }
        }
      }
    }
    return bbox;
  }

  calculateBoundingBoxForCells(cellIds) {
    const worldWidth = this.width;
    const firstId = cellIds[0];
    let minX = firstId % worldWidth;
    let maxX = minX;
    let minY = Math.floor(firstId / worldWidth);
    let maxY = minY;
    for (let i = 1, l = cellIds.length; i < l; i++) {
      const cellId = cellIds[i];
      const cellX = cellId & worldWidth;
      const cellY = Math.floor(cellId / worldWidth);
      if (minX > cellX) minX = cellX;
      if (minY > cellY) minY = cellY;
      if (maxX < cellX) maxX = cellX;
      if (maxY < cellY) maxY = cellY;
    }
    return { minX, minY, maxX, maxY, width: maxX - minX + 1, height: maxY - minY + 1 };
  }

  cellIdsForBoundingBox(bbox) {
    const cellIds = [];
    for (let y = bbox.minY; y <= bbox.maxY; y++) {
      for (let x = bbox.minX; x <= bbox.maxX; x++) {
        cellIds.push(this.xyToCellId(x, y));
      }
    }
    return cellIds;
  }

  // Given a list of sorted cellIds, quickly calculate the bounding box by taking the smallest and largest cellId.
  calculateBoundingBoxFast(cellIds) {
    const worldWidth = this.width;
    const firstId = cellIds[0];
    const lastId = cellIds[cellIds.length - 1];
    let minX = firstId % worldWidth;
    let minY = Math.floor(firstId / worldWidth);
    let maxX = lastId % worldWidth;
    let maxY = Math.floor(lastId / worldWidth);
    return { minX, minY, maxX, maxY, width: maxX - minX + 1, height: maxY - minY + 1 };
  }

  calculateWorldBoundingBox() {
    const cellIds = [];
    for (const entity of this.entities) {
      cellIds.push(...entity.cellIds);
    }
    cellIds.sort((a, b) => a - b);
    return this.calculateBoundingBoxFast(cellIds);
  }

  // Split the cellIds of the entity into rectangular patches. Returns a list of lists of cellIds.
  calculatePatchesForEntity(entity) {
    // We reverse the cellIds since this is mostly used for garden plots where
    // we want the largest shapes to start at the top of the screen (so highest Y values).
    // The algorithm will then find the largest patches at the top.
    const cellIds = Array.from(entity.cellIds).sort((a, b) => b - a);
    const patches = [];
    const cellSet = new Set();
    for (const cellId of cellIds) {
      if (cellSet.has(cellId)) continue;
      cellSet.add(cellId);
      const currentPatch = new Set();
      currentPatch.add(cellId);
      const pt = this.cellIdToPoint(cellId);
      let maxX = pt.x;
      let maxY = pt.y;
      let minX = maxX;
      let minY = maxY;
      // Move horizontally
      for (;;) {
        const newCellId = this.xyToCellId(minX - 1, pt.y);
        if (!entity.cellIds.includes(newCellId)) break;
        minX -= 1;
        cellSet.add(newCellId);
        currentPatch.add(newCellId);
      }
      // Move vertically
      vertically: for (;;) {
        for (let x = maxX; x >= minX; x--) {
          const newCellId = this.xyToCellId(x, minY - 1);
          if (!entity.cellIds.includes(newCellId)) break vertically;
        }
        minY -= 1;
        // Mark the whole row.
        // (We can't do it above since we're not sure yet all horizontal rows will be part of that row)
        for (let x = maxX; x >= minX; x--) {
          const newCellId = this.xyToCellId(x, minY);
          cellSet.add(newCellId);
          currentPatch.add(newCellId);
        }
      }
      patches.push(Array.from(currentPatch).sort((a, b) => a - b));
    }
    return patches;
  }

  // addEntity(cells) {
  //   // Check if there is already an entity at the cell
  //   if (this.cellsOccupied) throw new Error('Cells are taken.');
  // }

  // improve(options) {
  //   const allImprovements = [];
  //   const newWorld = this.clone();
  //   const passes = [
  //     // DENSITY
  //     AddFloors,
  //     AddPublicBuildings,
  //     BuildPark,
  //     MoreApartments,
  //     SplitResidentials,
  //     BuildOpenSpace,

  //     // COLLECTIVITY
  //     CombineGardens,
  //     MorePeople,
  //     AddMarket,
  //     AddCommunityGarden,
  //     AddCoworking,
  //     BuildWindTurbine,

  //     // SMART LOCATION
  //     AddAmenities,
  //     MoreBusesLessCars,
  //     MoreBikesLessCars,
  //     AddUndergroundParking,
  //     BundleParking,

  //     // OTHER
  //     AddStreetParty // This has to go after road improvements
  //   ];
  //   if (featureEnabled('greenery')) {
  //     passes.push(AddRoadSideGreenery);
  //   }
  //   for (const pass of passes) {
  //     const improvements = pass(newWorld, options);
  //     allImprovements.push(...improvements);
  //   }
  //   newWorld.improvements = allImprovements;
  //   return newWorld;
  // }
}
